import React, { useState, useEffect } from 'react'
import { useLocation, useNavigate, Link } from 'react-router-dom'
import { HiOutlineArrowLeft } from 'react-icons/hi2'
import './PlanDetailsPage.scss'
import logoImage_1 from '../../assets/White PNG 1.png'
import logoImage_2 from '../../assets/Black PNG 1.png'
import { HiEye, HiEyeSlash, HiCheckCircle, HiXCircle, HiExclamationCircle, HiNoSymbol } from 'react-icons/hi2'

interface PlanDetails {
  id: number
  title: string
  duration?: number
  start_at: number | null
  end_at: number | null
}

interface UserAccount {
  id: number
  account_number: string
  account_server: string
  is_active: boolean
  plan: PlanDetails | null
}

type AccountStatusObj = {
  status: 'active_plan' | 'no_plan' | 'inactive_plan' | 'expired' | 'pending_activation'
  color: string
  bg: string
  border: string
  text: string
  icon: React.ReactNode
  selectable: boolean
}

const AddAccountPage: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const queryParams = new URLSearchParams(location.search)

  const theme = queryParams.get('theme') || 'dark'
  const planId = parseInt(queryParams.get('planId') || '0')
  const planName = queryParams.get('planName') || 'نامشخص'
  const planDuration = parseInt(queryParams.get('planDuration') || '0')
  const periodLabel = queryParams.get('periodLabel') || 'نامشخص'
  const fromUserPanel = queryParams.get('fromUserPanel') === 'true'
  const [showPassword, setShowPassword] = useState(false)
  const [formData, setFormData] = useState({
    account_number: '',
    account_password: '',
    account_server: '',
    broker_website: '',
    account_type: 'personal'
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [userAccounts, setUserAccounts] = useState<UserAccount[]>([])
  const [showAddAccountForm, setShowAddAccountForm] = useState(false)
  const [accountsLoading, setAccountsLoading] = useState(true)

  useEffect(() => {
    if (fromUserPanel) {
      setAccountsLoading(false)
      setShowAddAccountForm(true)
      return
    }
    const fetchUserAccounts = async () => {
      setAccountsLoading(true)
      const token = localStorage.getItem('token')
      if (!token) {
        setError('توکن احراز هویت یافت نشد.')
        setAccountsLoading(false)
        return
      }
      try {
        const response = await fetch('https://api.neontradepro.com/trade-account', {
          headers: { Authorization: `Bearer ${token}` }
        })
        if (!response.ok) throw new Error((await response.json()).detail || 'خطا در دریافت حساب‌ها.')
        const accounts: UserAccount[] = await response.json()

        setUserAccounts(accounts)

        if (accounts.length === 0) {
          setShowAddAccountForm(true)
        }
      } catch (err: any) {
        setError(err.message)
      } finally {
        setAccountsLoading(false)
      }
    }
    fetchUserAccounts()
  }, [fromUserPanel])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }))
  }

  const navigateToConfirm = (account: UserAccount) => {
    navigate(
      `/confirm-purchase?theme=${theme}&planId=${planId}&planName=${encodeURIComponent(
        planName
      )}&planDuration=${planDuration}&periodLabel=${encodeURIComponent(periodLabel)}&accountId=${
        account.id
      }&accountNumber=${account.account_number}&accountServer=${encodeURIComponent(account.account_server)}`
    )
  }

  const getAccountStatusInfo = (account: UserAccount): AccountStatusObj => {
    const now = Math.floor(Date.now() / 1000)

    if (!account.plan || !account.plan.title) {
      return {
        status: 'no_plan',
        color: '#dc3545',
        bg: 'rgba(220, 53, 69, 0.1)',
        border: 'rgba(220, 53, 69, 0.3)',
        text: 'پلن ندارد',
        icon: <HiXCircle />,
        selectable: true
      }
    }

    const plan = account.plan

    if (!plan.start_at || !plan.end_at) {
      return {
        status: 'pending_activation',
        color: '#ffc107',
        bg: 'rgba(255, 193, 7, 0.1)',
        border: 'rgba(255, 193, 7, 0.3)',
        text: `در انتظار فعالسازی (${plan.title})`,
        icon: <HiExclamationCircle />,
        selectable: true
      }
    }

    if (plan.end_at < now) {
      return {
        status: 'expired',
        color: '#6c757d',
        bg: 'rgba(108, 117, 125, 0.1)',
        border: 'rgba(108, 117, 125, 0.3)',
        text: `منقضی شده (${plan.title})`,
        icon: <HiNoSymbol />,
        selectable: true
      }
    }

    if (plan.start_at <= now && plan.end_at > now) {
      if (account.is_active === true) {
        return {
          status: 'active_plan',
          color: '#28a745',
          bg: 'rgba(40, 167, 69, 0.1)',
          border: 'rgba(40, 167, 69, 0.3)',
          text: `پلن فعال دارد (${plan.title})`,
          icon: <HiCheckCircle />,
          selectable: false
        }
      } else {
        return {
          status: 'inactive_plan',
          color: '#ffc107',
          bg: 'rgba(255, 193, 7, 0.1)',
          border: 'rgba(255, 193, 7, 0.3)',
          text: `غیرفعال شده توسط ادمین (${plan.title})`,
          icon: <HiExclamationCircle />,
          selectable: true
        }
      }
    }

    if (plan.start_at > now) {
      return {
        status: 'pending_activation',
        color: '#ffc107',
        bg: 'rgba(255, 193, 7, 0.1)',
        border: 'rgba(255, 193, 7, 0.3)',
        text: `در انتظار شروع (${plan.title})`,
        icon: <HiExclamationCircle />,
        selectable: true
      }
    }

    return {
      status: 'inactive_plan',
      color: '#ffc107',
      bg: 'rgba(255, 193, 7, 0.1)',
      border: 'rgba(255, 193, 7, 0.3)',
      text: `غیرفعال / در انتظار فعالسازی (${plan.title})`,
      icon: <HiExclamationCircle />,
      selectable: true
    }
  }

  const handleAccountSelect = (account: UserAccount, isSelectable: boolean) => {
    if (isSelectable) {
      navigateToConfirm(account)
    }
  }

  const handleSubmitNewAccount = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    const token = localStorage.getItem('token')
    if (!token) {
      setError('توکن احراز هویت یافت نشد.')
      setLoading(false)
      return
    }
    try {
      const response = await fetch('https://api.neontradepro.com/trade-account', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      })

      const responseData = await response.json()

      if (!response.ok) {
        throw new Error(responseData.detail || 'خطا در ثبت حساب.')
      }

      const newAccount = responseData

      if (fromUserPanel) {
        navigate('/dashboard', { state: { newAccountAdded: true, tab: 1 } })
      } else {
        navigateToConfirm({
          id: newAccount.id,
          account_number: newAccount.account_number || formData.account_number,
          account_server: newAccount.account_server || formData.account_server,
          is_active: newAccount.is_active,
          plan: null
        })
      }
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const pageHeaderStyle: React.CSSProperties = {
    padding: '0.75rem 2rem',
    borderBottom: '1px solid var(--border-color, #333)',
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    position: 'sticky',
    top: 0,
    zIndex: 1000,
    backgroundColor: theme === 'dark' ? '#121212' : '#f0f0f0',
    direction: 'rtl'
  }

  const logoInHeaderStyle: React.CSSProperties = {
    height: '30px',
    display: 'block'
  }

  return (
    <>
      <header style={pageHeaderStyle}>
        <div className='logo'>
          <Link to={`/?theme=${theme}`}>
            <img src={theme === 'dark' ? logoImage_1 : logoImage_2} alt='لوگوی نئون' style={logoInHeaderStyle} />
          </Link>
        </div>
      </header>
      <div className={`plan-details-page ${theme}`}>
        <div className='details-container'>
          <div className='details-header'>
            <h1> افزودن یا انتخاب حساب</h1>
          </div>
          {!fromUserPanel && (
            <div
              className='plan-summary'
              style={{
                marginBottom: '2rem',
                padding: '1rem',
                background: 'var(--pdp-feature-card-bg)',
                borderRadius: '8px',
                border: '1px solid var(--border-color)'
              }}
            >
              <p style={{ margin: '0.5rem 0' }}>
                <strong>پلن انتخابی:</strong> {planName}
              </p>
              <p style={{ margin: '0.5rem 0' }}>
                <strong>دوره انتخابی:</strong> {periodLabel}
              </p>
            </div>
          )}
          {accountsLoading ? (
            <p style={{ textAlign: 'center' }}>در حال بارگیری...</p>
          ) : error ? (
            <p style={{ textAlign: 'center', color: 'red' }}>{error}</p>
          ) : showAddAccountForm ? (
            <form onSubmit={handleSubmitNewAccount} className='account-form'>
              <h3 style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                {userAccounts.length > 0 ? 'یا افزودن یک حساب جدید' : 'برای ادامه، لطفاً یک حساب جدید اضافه کنید'}
              </h3>
              <div className='input-group'>
                <label htmlFor='account_number'>شماره حساب</label>
                <input
                  type='text'
                  id='account_number'
                  name='account_number'
                  value={formData.account_number}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className='input-group' style={{ position: 'relative' }}>
                <label htmlFor='account_password'>رمز عبور</label>

                <input
                  type={showPassword ? 'text' : 'password'}
                  id='account_password'
                  name='account_password'
                  value={formData.account_password}
                  onChange={handleInputChange}
                  required
                  style={{ paddingLeft: '45px' }}
                />

                <button
                  type='button'
                  onClick={() => setShowPassword(prev => !prev)}
                  style={{
                    position: 'absolute',
                    left: '10px',
                    top: '72px',
                    transform: 'translateY(-100%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '20px',
                    color: 'var(--text-muted-color)'
                  }}
                >
                  {showPassword ? <HiEyeSlash /> : <HiEye />}
                </button>
              </div>

              <div className='input-group'>
                <label htmlFor='account_server'>سرور</label>
                <input
                  type='text'
                  id='account_server'
                  name='account_server'
                  value={formData.account_server}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className='input-group'>
                <label htmlFor='broker_website'>بروکر</label>
                <input
                  type='text'
                  id='broker_website'
                  name='broker_website'
                  value={formData.broker_website}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className='input-group'>
                <label htmlFor='account_type'>نوع حساب</label>
                <select
                  id='account_type'
                  name='account_type'
                  value={formData.account_type}
                  onChange={handleInputChange}
                  required
                >
                  <option value='personal'>شخصی</option>
                  <option value='prop'>پراپ</option>
                </select>
              </div>
              <button
                type='submit'
                className='select-plan-btn-details'
                disabled={loading}
                style={{
                  marginTop: '1rem',
                  background: 'linear-gradient(to right, hsl(199, 100%, 50%), hsl(324, 100%, 50%))',
                  padding: '12px'
                }}
              >
                {loading ? '...' : 'ثبت و ادامه'}
              </button>
              {userAccounts.length > 0 && !fromUserPanel && (
                <button
                  type='button'
                  onClick={() => setShowAddAccountForm(false)}
                  className='details-back-link'
                  style={{
                    background: 'transparent',
                    border: '1px solid var(--border-color)',
                    color: 'var(--text-muted-color)'
                  }}
                >
                  <HiOutlineArrowLeft /> بازگشت به لیست
                </button>
              )}
            </form>
          ) : (
            <div>
              <h3 style={{ marginBottom: '1.5rem', textAlign: 'center' }}>یک حساب را برای ادامه انتخاب کنید</h3>
              {userAccounts.map(account => {
                const info = getAccountStatusInfo(account)
                return (
                  <div
                    key={account.id}
                    style={{
                      padding: '1rem',
                      border: `1px solid ${info.border}`,
                      backgroundColor: info.bg,
                      borderRadius: '8px',
                      marginBottom: '1rem',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      position: 'relative',
                      opacity: info.status === 'expired' ? 0.7 : 1,
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <p style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <strong>شماره:</strong> {account.account_number}
                      </p>
                      <p style={{ margin: '0.2rem 0', fontSize: '0.9rem', color: 'var(--text-muted-color)' }}>
                        <strong>سرور:</strong> {account.account_server}
                      </p>

                      <div
                        style={{
                          marginTop: '0.5rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.3rem',
                          color: info.color,
                          fontWeight: 'bold',
                          fontSize: '0.85rem'
                        }}
                      >
                        <span style={{ fontSize: '1.2rem', display: 'flex' }}>{info.icon}</span>
                        {info.text}
                      </div>
                    </div>

                    <button
                      onClick={() => handleAccountSelect(account, info.selectable)}
                      disabled={!info.selectable}
                      className='select-plan-btn-details'
                      style={{
                        minWidth: '120px',
                        background: info.selectable
                          ? 'linear-gradient(to right, hsl(199, 100%, 50%), hsl(324, 100%, 50%))'
                          : '#444',
                        padding: '12px',
                        cursor: info.selectable ? 'pointer' : 'not-allowed',
                        opacity: info.selectable ? 1 : 0.5,
                        color: '#fff'
                      }}
                    >
                      {info.selectable ? 'انتخاب' : 'غیرقابل انتخاب'}
                    </button>
                  </div>
                )
              })}
              <hr style={{ margin: '2rem 0', borderTop: '1px solid var(--border-color)' }} />
              <button
                onClick={() => setShowAddAccountForm(true)}
                className='details-back-link'
                style={{ width: '100%', justifyContent: 'center' }}
              >
                افزودن یک حساب دیگر
              </button>
              <div style={{ textAlign: 'center', marginTop: '3rem' }}>
                <Link to={`/plan-details/${planId}?${queryParams.toString()}`} className='details-back-link'>
                  <HiOutlineArrowLeft /> بازگشت به مرحله قبل
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
export default AddAccountPage
